import type { DocumentNode, FragmentDefinitionNode } from 'graphql';
import { isNull } from 'lodash';
import { useFragment } from '@frameio/graphql-client';
import type {
  Project,
  ProjectPermissions,
} from '@frameio/graphql-client/generated';
import {
  CanCopyAssetPermissionForProject,
  CanCopyCommentPermissionForProject,
  CanCreateAssetPermissionForProject,
  CanCreateCaptionPermissionForProject,
  CanCreateCollectionPermissionForProject,
  CanCreateCommentAttachmentPermissionForProject,
  CanCreateCommentPermissionForProject,
  CanCreateDeviceForProject,
  CanCreateTranscriptionPermissionForProject,
  CanDeleteAssetPermissionForProject,
  CanDeleteCaptionPermissionForProject,
  CanDeleteCollectionPermissionForProject,
  CanDeleteCommentAttachmentPermissionForProject,
  CanDeleteCommentPermissionForProject,
  CanDeleteDeviceForProject,
  CanDeletePermissionForProject,
  CanDeleteTranscriptionPermissionForProject,
  CanDownloadAssetPermissionForProject,
  CanDownloadCaptionPermissionForProject,
  CanDownloadCommentAttachmentPermissionForProject,
  CanDownloadCommentPermissionForProject,
  CanDownloadTranscriptionPermissionForProject,
  CanEditTranscriptionPermissionForProject,
  CanEditUserPermissionsPermissionForProject,
  CanManageAssetMetadataPermissionForProject,
  CanManageResourceControlPoliciesForProject,
  CanMountPermissionForProject,
  CanMoveAssetPermissionForProject,
  CanReactToCommentPermissionForProject,
  CanSharePermissionForProject,
  CanUpdateAssetPermissionForProject,
  CanUpdateCaptionPermissionForProject,
  CanUpdateCollectionPermissionForProject,
  CanUpdateCommentStatusPermissionForProject,
  CanUpdateDeviceForProject,
  CanUpdatePermissionForProject,
  CanVersionAssetPermissionForProject,
  CanViewAssetPermissionForProject,
  CanViewCaptionPermissionForProject,
  CanViewComment,
  CanViewDeviceForProject,
  CanViewTranscriptionPermissionForProject,
  CanViewUserGroupPermissionForProject,
  CanViewUserPermissionsPermissionForProject,
  CanViewWorkspacePermissionForProject,
} from '@frameio/graphql-client/generated';

type UseProjectPermissionsArgs = {
  /** The permission field to check */
  check: keyof Omit<ProjectPermissions, '__typename'>;
  /** Whether to consider the permission true initially prior to data resolving */
  isInitiallyPermitted?: boolean;
  /** The id of the Project to check against */
  projectId: Project['id'];
};

/**
 * Reactive hook to read Project-level permissions directly off the cache
 * @param {check, isInitiallyPermitted, projectId}
 * @returns A boolean of whether a given check for the Project with the given `projectId` is true
 */
export const useProjectPermissions = ({
  check,
  isInitiallyPermitted = false,
  projectId,
}: UseProjectPermissionsArgs): boolean | null => {
  let fragment: DocumentNode;
  switch (check) {
    case 'canCopyAsset':
      fragment = CanCopyAssetPermissionForProject;
      break;
    case 'canCreateAsset':
      fragment = CanCreateAssetPermissionForProject;
      break;
    case 'canCopyComment':
      fragment = CanCopyCommentPermissionForProject;
      break;
    case 'canCreateComment':
      fragment = CanCreateCommentPermissionForProject;
      break;
    case 'canCreateCommentAttachment':
      fragment = CanCreateCommentAttachmentPermissionForProject;
      break;
    case 'canDelete':
      fragment = CanDeletePermissionForProject;
      break;
    case 'canDeleteAsset':
      fragment = CanDeleteAssetPermissionForProject;
      break;
    case 'canDeleteComment':
      fragment = CanDeleteCommentPermissionForProject;
      break;
    case 'canDeleteCommentAttachment':
      fragment = CanDeleteCommentAttachmentPermissionForProject;
      break;
    case 'canDownloadAsset':
      fragment = CanDownloadAssetPermissionForProject;
      break;
    case 'canDownloadCommentAttachment':
      fragment = CanDownloadCommentAttachmentPermissionForProject;
      break;
    case 'canDownloadComment':
      fragment = CanDownloadCommentPermissionForProject;
      break;
    case 'canEditUserPermissions':
      fragment = CanEditUserPermissionsPermissionForProject;
      break;
    case 'canMount':
      fragment = CanMountPermissionForProject;
      break;
    case 'canMoveAsset':
      fragment = CanMoveAssetPermissionForProject;
      break;
    case 'canReactToComment':
      fragment = CanReactToCommentPermissionForProject;
      break;
    case 'canShare':
      fragment = CanSharePermissionForProject;
      break;
    case 'canUpdate':
      fragment = CanUpdatePermissionForProject;
      break;
    case 'canUpdateAsset':
      fragment = CanUpdateAssetPermissionForProject;
      break;
    case 'canUpdateCommentStatus':
      fragment = CanUpdateCommentStatusPermissionForProject;
      break;
    case 'canVersionAsset':
      fragment = CanVersionAssetPermissionForProject;
      break;
    case 'canViewAsset':
      fragment = CanViewAssetPermissionForProject;
      break;
    case 'canManageAssetMetadata':
      fragment = CanManageAssetMetadataPermissionForProject;
      break;
    case 'canViewUserGroup':
      fragment = CanViewUserGroupPermissionForProject;
      break;
    case 'canViewUserPermissions':
      fragment = CanViewUserPermissionsPermissionForProject;
      break;
    case 'canCreateDevice':
      fragment = CanCreateDeviceForProject;
      break;
    case 'canViewDevice':
      fragment = CanViewDeviceForProject;
      break;
    case 'canDeleteDevice':
      fragment = CanDeleteDeviceForProject;
      break;
    case 'canUpdateDevice':
      fragment = CanUpdateDeviceForProject;
      break;
    case 'canViewWorkspace':
      fragment = CanViewWorkspacePermissionForProject;
      break;
    case 'canViewComment':
      fragment = CanViewComment;
      break;
    case 'canDeleteCollection':
      fragment = CanDeleteCollectionPermissionForProject;
      break;
    case 'canCreateCollection':
      fragment = CanCreateCollectionPermissionForProject;
      break;
    case 'canUpdateCollection':
      fragment = CanUpdateCollectionPermissionForProject;
      break;
    case 'canManageResourceControlPolicies':
      fragment = CanManageResourceControlPoliciesForProject;
      break;
    case 'canViewTranscription':
      fragment = CanViewTranscriptionPermissionForProject;
      break;
    case 'canCreateTranscription':
      fragment = CanCreateTranscriptionPermissionForProject;
      break;
    case 'canEditTranscription':
      fragment = CanEditTranscriptionPermissionForProject;
      break;
    case 'canDeleteTranscription':
      fragment = CanDeleteTranscriptionPermissionForProject;
      break;
    case 'canDownloadTranscription':
      fragment = CanDownloadTranscriptionPermissionForProject;
      break;
    case 'canViewCaption':
      fragment = CanViewCaptionPermissionForProject;
      break;
    case 'canCreateCaption':
      fragment = CanCreateCaptionPermissionForProject;
      break;
    case 'canUpdateCaption':
      fragment = CanUpdateCaptionPermissionForProject;
      break;
    case 'canDeleteCaption':
      fragment = CanDeleteCaptionPermissionForProject;
      break;
    case 'canDownloadCaption':
      fragment = CanDownloadCaptionPermissionForProject;
      break;
    default:
      break;
  }

  const fragmentNode = fragment.definitions[0] as FragmentDefinitionNode;

  const result = useFragment({
    fragment,
    fragmentName: fragmentNode.name.value,
    from: {
      __typename: 'Project',
      id: projectId ?? '',
    },
  });

  // If the permission does not exist yet, we want to check against that rather than defaulting to false
  const isPermissionUnresolved =
    isNull(result) ||
    Object.keys(result?.data)?.length < 1 ||
    !result?.complete;
  /**
   * Return the result of the fragment, and fall back to the prop that controls whether we consider the permission true
   * prior to the permission being resolved
   */
  return isPermissionUnresolved
    ? isInitiallyPermitted
    : result.data?.permissions?.[check];
};
